/**
 * Ads — MOCK implementation for the web prototype (docs/MONETIZATION.md § 2).
 *
 * - Banners are static, clearly labelled "Ad" placeholders drawn by the app:
 *   never animated, never refreshed, and gone the moment ads are removed
 *   (Remove Ads or an active Plus). The optional landscape `play-side-banner`
 *   is off unless explicitly enabled.
 * - Rewarded ads are opt-in and cosmetic only. The mock has no ad network: the
 *   app registers a presenter (a modal that says "Sample ad (prototype)") and
 *   showRewarded() resolves with what the player did there. A few per day at most.
 *
 * A real integration (AdMob) replaces createAdsService() with an adapter that
 * implements the same AdsService contract; see docs/MONETIZATION.md § 6.5.
 */
import type { AdPlacement, AdsService, PurchaseService, RewardedPlacement } from '../contracts.ts';
import { t } from '../i18n/index.ts';
import { getAppStorage, isFiniteNumber, isRecord, type JsonStore } from '../state/storage.ts';

export type RewardedOutcome = Awaited<ReturnType<AdsService['showRewarded']>>;

/** Shows the (sample) rewarded ad and reports whether the player watched it to the end. */
export type RewardedPresenter = (placement: RewardedPlacement) => Promise<'completed' | 'skipped'>;

/** Rewarded ads a player can watch per day (docs/MONETIZATION.md: "cap it at a few a day"). */
export const DAILY_REWARDED_CAP = 3;

/** Where the daily rewarded count is kept (device only). */
export const ADS_STORAGE_KEY = 'dropstack.ads.v1';

export interface AdsServiceOptions {
  store?: JsonStore;
  /** Wall clock for the daily cap (default Date.now). */
  now?: () => number;
  dailyRewardedCap?: number;
  /** Allow the optional landscape side banner during play (default false). */
  playSideBanner?: boolean;
}

export interface MockAdsService extends AdsService {
  /** Mock provider: banners and rewarded ads are samples, not real ads. */
  readonly testMode: true;
  /** Register the UI that shows a rewarded ad. Returns an unregister function. */
  setRewardedPresenter(presenter: RewardedPresenter | null): () => void;
  /** Rewarded ads still available today (0 when ads are off). */
  rewardedLeftToday(): number;
}

interface RewardedLedger {
  /** Local calendar day, "YYYY-MM-DD". */
  day: string;
  count: number;
}

export function createAdsService(purchases: PurchaseService, options: AdsServiceOptions = {}): MockAdsService {
  const store = options.store ?? getAppStorage();
  const now = options.now ?? Date.now;
  const cap = options.dailyRewardedCap ?? DAILY_REWARDED_CAP;
  const sideBanner = options.playSideBanner ?? false;
  let presenter: RewardedPresenter | null = null;
  let showing = false;

  const enabled = () => !purchases.getEntitlements().adsRemoved;

  const usedToday = (): number => {
    const ledger = store.get(ADS_STORAGE_KEY, decodeLedger);
    return ledger && ledger.day === localDay(now()) ? ledger.count : 0;
  };

  return {
    testMode: true,

    get enabled() {
      return enabled();
    },

    showBanner(slot: AdPlacement, el: HTMLElement): () => void {
      if (!enabled() || (slot === 'play-side-banner' && !sideBanner)) return noop;
      const banner = renderBanner(slot, el.ownerDocument);
      el.append(banner);
      // Buying Remove Ads / Plus takes the banner away at once, without a reload.
      const unsubscribe = purchases.subscribe((e) => {
        if (e.adsRemoved) banner.remove();
      });
      return () => {
        unsubscribe();
        banner.remove();
      };
    },

    async showRewarded(placement: RewardedPlacement): Promise<RewardedOutcome> {
      if (!enabled() || !presenter || showing || usedToday() >= cap) return 'unavailable';
      showing = true;
      try {
        const outcome = await presenter(placement);
        if (outcome === 'completed') store.set(ADS_STORAGE_KEY, { day: localDay(now()), count: usedToday() + 1 });
        return outcome;
      } catch {
        return 'unavailable';
      } finally {
        showing = false;
      }
    },

    setRewardedPresenter(next) {
      presenter = next;
      return () => {
        if (presenter === next) presenter = null;
      };
    },

    rewardedLeftToday() {
      return enabled() ? Math.max(0, cap - usedToday()) : 0;
    },
  };
}

function noop(): void {}

/** A static, labelled placeholder banner (the label is drawn by the app, as the policy requires). */
function renderBanner(slot: AdPlacement, doc: Document): HTMLElement {
  const banner = doc.createElement('aside');
  banner.className = 'ui-ad';
  banner.dataset.slot = slot;
  banner.setAttribute('aria-label', t('common.adAria'));

  const label = doc.createElement('span');
  label.className = 'ui-ad__label';
  label.textContent = t('common.adLabel');

  const body = doc.createElement('span');
  body.className = 'ui-ad__body';
  const title = doc.createElement('span');
  title.className = 'ui-ad__title';
  title.textContent = t('common.adSampleTitle');
  const text = doc.createElement('span');
  text.className = 'ui-ad__text';
  text.textContent = t('common.adSampleText');
  body.append(title, text);

  banner.append(label, body);
  return banner;
}

/** Local calendar day of an epoch-ms time, "YYYY-MM-DD". */
export function localDay(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function decodeLedger(raw: unknown): RewardedLedger | null {
  if (!isRecord(raw) || typeof raw.day !== 'string' || !isFiniteNumber(raw.count)) return null;
  return { day: raw.day, count: Math.max(0, Math.floor(raw.count)) };
}
